import { apiSlice } from '../api/apiSlice';
import formatRelativeTime from '../../helpers/timeAgo';

export const timelineApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Likes and Dislikes ──
    likeDislikePost: builder.mutation({
      query: (body) => ({
        url: 'timeline/likedislike',
        method: 'PUT',
        body,
      }),
    }),
    getPostInteractions: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams(params).toString();
        return `timeline/getAllonPost?${queryParams}`;
      },
    }),

    // ── Post Management ──
    deletePost: builder.mutation({
      query: ({ postId, timelineId }) => ({
        url: `/timeline/?postId=${postId}&timelineId=${timelineId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const { postId, userid, shopid, businessType } = arg || {};
        let patchResult;
        try {
          // Try optimistic removal from matching timeline cache
          if (userid || shopid || businessType) {
            patchResult = dispatch(
              apiSlice.util.updateQueryData(
                'getTimelinePosts',
                { userid, shopid, businessType },
                (draft) => {
                  if (!draft || !draft.data) return;
                  draft.data.forEach((tl) => {
                    if (Array.isArray(tl.my)) {
                      tl.my = tl.my.filter((p) => (p?._id || p)?.toString() !== postId?.toString());
                    }
                    if (Array.isArray(tl.sharebyother)) {
                      tl.sharebyother = tl.sharebyother.filter((s) => (s?.postid?._id || s?.postid)?.toString() !== postId?.toString());
                    }
                    if (Array.isArray(tl.sharedtoother)) {
                      tl.sharedtoother = tl.sharedtoother.filter((s) => (s?.postid?._id || s?.postid)?.toString() !== postId?.toString());
                    }
                  });
                }
              )
            );
          }
          await queryFulfilled;
        } catch (err) {
          if (patchResult && patchResult.undo) patchResult.undo();
        }
      },
      invalidatesTags: ['Timeline'],
    }),
    updatePost: builder.mutation({
      query: ({ timelineId, postId, body }) => ({
        url: `timeline/updateMyPost?timelineId=${timelineId}&postId=${postId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Timeline'],
    }),

    // ── Comments ──
    getTimelineComments: builder.query({
      query: (postId) => `/timeline/getShopSuggestion/?postid=${postId}`,
    }),
    addComment: builder.mutation({
      query: (body) => ({
        url: `/timeline/addcomment`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Timeline'],
    }),

    replySuggestion: builder.mutation({
      query: ({ suggestionId, reply }) => ({
        url: `/timeline/reply/suggestionId?suggestionId=${suggestionId}`,
        method: 'PUT',
        body: { reply },
      }),
    }),

    // ── Sharing ──
    getShareTimelineConnections: builder.query({
      query: ({ shopid, userid, isMarketerProfile } = {}) => {
        const params = new URLSearchParams();
        if (userid) params.append('userid', userid);
        if (shopid != null && shopid !== '' && shopid !== 'null' && shopid !== 'undefined') {
          params.append('shopid', shopid);
        }
        if (
          isMarketerProfile === true ||
          isMarketerProfile === 'true'
        ) {
          params.append('isMarketerProfile', 'true');
        }
        return `timeline/getAllConnectedShopstype?${params.toString()}`;
      },
    }),
    timelineShare: builder.mutation({
      query: (body) => ({
        url: `timeline/timelineShare`,
        method: 'PATCH',
        body,
      }),
      // Share-count bump is handled in useShareOption (unique recipients only).
      // Do not bump by selected businessType length — that inflated the count.
      // Refresh marketing shop statistics ("Shared to no of users").
      // Invalidate both the specific shop tag and the whole type so personal
      // shares (shopid null) still refresh the open marketing shop stats.
      invalidatesTags: (result, error, arg) => [
        'Timeline',
        'VisitProfile',
        'SubscriptionMarketing',
        { type: 'SubscriptionMarketing', id: arg?.shopid || 'LIST' },
      ],
    }),
    // ── Get Timeline Posts ──
    getTimelinePosts: builder.query({
      query: ({ userid, shopid, businessType } = {}) => {
        const params = new URLSearchParams();
        if (userid) params.append('userid', userid);
        if (shopid) params.append('shopid', shopid);
        if (businessType) params.append('businessType', businessType);
        const q = params.toString();
        return `timeline/getAll${q ? `?${q}` : ''}`;
      },
      providesTags: ['Timeline'],
    }),
    // ── Create Post ──
    createPost: builder.mutation({
      query: (body) => ({
        url: `timeline/`,
        method: 'POST',
        body,
      }),
      // Optimistic update: immediately update the timeline cache for the
      // specific query args that match the mutation input, then undo on failure.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        let patchResult;
        try {
          patchResult = dispatch(
            apiSlice.util.updateQueryData(
              'getTimelinePosts',
              { userid: arg.userid, shopid: arg.shopid, businessType: arg.businessType },
              (draft) => {
                try {
                  if (!draft) return;
                  // server response shape: { data: [ timelineObj, ... ] }
                  if (!draft.data) {
                    draft.data = [];
                  }
                  const tl = draft.data[0];
                  const newPostData = arg.my?.[0] || {};

                  const optimisticPost = {
                    _id: `optimistic-${Date.now()}`,
                    createdAt: new Date().toISOString(),
                    userid: arg.userid || null,
                    ...newPostData,
                  };

                  // attach client-side timeAgo for immediate display
                  optimisticPost.timeAgo = formatRelativeTime(optimisticPost.createdAt);

                  if (tl) {
                    tl.my = [optimisticPost, ...(tl.my || [])];
                  } else {
                    draft.data[0] = {
                      _id: `optimistic-tl-${Date.now()}`,
                      my: [optimisticPost],
                      sharebyother: [],
                      businessType: arg.businessType || null,
                    };
                  }
                } catch (e) {
                  console.error('timeline optimistic patch error', e);
                }
              }
            )
          );

          await queryFulfilled;
          // success: real server response will update cache via invalidation
          // nothing else required here
        } catch (err) {
          // if the mutation failed, undo the optimistic update
          // Note: updateQueryData returns an undo patch, but we captured it inside patchResult variable
          if (patchResult && patchResult.undo) patchResult.undo();
        }
      },
      invalidatesTags: ['Timeline'],
    }),
  }),
});

export const {
  useGetTimelinePostsQuery,
  useCreatePostMutation,
  useLikeDislikePostMutation,
  useLazyGetPostInteractionsQuery,
  useDeletePostMutation,
  useUpdatePostMutation,
  useLazyGetTimelineCommentsQuery,
  useAddCommentMutation,
  useReplySuggestionMutation,
  useLazyGetShareTimelineConnectionsQuery,
  useTimelineShareMutation,
} = timelineApiSlice;
